import {
  compact,
  difference,
  last,
  partition,
  uniq,
  zip,
  findLast,
} from "lodash";

import generateUUID from "../../generateUUID";

import { convertAllInlineNodesToRangeJSON } from "../Convert/RangeInlineJSON";

import * as DocConvertPlainText from "../Convert/PlainText";

type SectionWithContinueUid = DocSectionType<InlineNode> & {
  continue_button_uid?: string;
};

export function reflow(data: DocSectionType<InlineNode>, documentId: number, options: {showCardTest?: (card: DocSectionType<InlineNode>) => boolean} = {}) {
  const root_section = data;

  let end_of_case_text = root_section.content.find((node) => node.uid === "end_of_case_text");
  if (!end_of_case_text) {
    end_of_case_text = endOfCaseText(root_section.title);
  }

  let cards = root_section.content.filter((node): node is DocSectionType<InlineNode> => node.type === "section");
  cards.forEach((section) => {
    section.classes = uniq((section.classes || []).concat(["card"]));
  });

  let feedback_cards: DocSectionType<InlineNode>[];
  let finish_case_cards: DocSectionType<InlineNode>[];
  let release_notes_cards: DocSectionType<InlineNode>[];
  let learning_objectives_cards: DocSectionType<InlineNode>[];
  let case_synopsis_cards: DocSectionType<InlineNode>[];
  let references_cards: DocSectionType<InlineNode>[];
  let saq_cards: DocSectionType<InlineNode>[];
  let regular_cards: DocSectionType<InlineNode>[];

  let remaining_cards = cards;
  [feedback_cards, remaining_cards] = partitionByClass(remaining_cards, "feedback_card");
  [finish_case_cards, remaining_cards] = partitionByClass(remaining_cards, "finish_case_card");
  let finish_case_card: DocSectionType<InlineNode> = finish_case_cards[0] || finishCaseCard();
  [release_notes_cards, remaining_cards] = partitionByClass(remaining_cards, "release_notes_card");
  [learning_objectives_cards, remaining_cards] = partition(remaining_cards, (node) => (/Learning Objectives/i).test(getText(node.title)));
  [case_synopsis_cards, remaining_cards] = partitionByClass(remaining_cards, "case_synopsis_card");
  [references_cards, remaining_cards] = partition(remaining_cards, (node) => !!(node.classes && node.classes.includes("references_card") && (/References/).test(getText(node.title))));
  [saq_cards, remaining_cards] = partitionByClass(remaining_cards, "saq_card");
  regular_cards = remaining_cards;

  if (feedback_cards.length === 0) { feedback_cards = [feedbackCard()]; }
  if (release_notes_cards.length === 0) { release_notes_cards = releaseNotesCards(); }
  if (learning_objectives_cards.length === 0) { learning_objectives_cards = learningObjectivesCards(); }
  if (case_synopsis_cards.length === 0) { case_synopsis_cards = caseSynopsisCards(); }

  const hidden_cards = case_synopsis_cards.concat(references_cards);
  hidden_cards.forEach((section) => {
    section.invisible = true;
  });

  let finish_case_text: DocParagraphType<InlineNode> | undefined;
  let case_summary_button: DocButtonType<InlineNode> | undefined;
  let finish_case_saq_text: DocParagraphType<InlineNode> | undefined;

  cards.forEach((section) => {
    section.content = section.content.filter((node) => {
      if ( node.type === "paragraph" && (
          (/Well done! You have completed/i).test(getText(node.content))
          || (node.classes && node.classes.includes("finish_case_text"))
      )) {
        finish_case_text = node;
        return false;
      } else if (node.type === "button" && node.classes && node.classes.includes("case_summary_button")) {
        case_summary_button = node;
        return false;
      } else if ( node.type === "paragraph" && (
        getText(node.content)=== "You may now continue to the optional self-assessment questions for you to assess what you have learned in this case."
        || getText(node.content) === "You may now continue to the  self-assessment questions for you to assess what you have learned in this case."
        || (node.classes && node.classes.includes("finish_case_saq_text"))
      )) {
        finish_case_saq_text = node;
        return false;
      } else if (node.uid === "feedback_introduction_text") {
        return false;
      } else {
        return true;
      }
    });
  });

  const not_continue_cards = difference(cards, regular_cards);
  not_continue_cards.forEach((section) => {
    section.content = section.content.filter((node) => !(node.classes && node.classes.includes("continue_button")));
  });

  const not_feedback_cards = difference(cards, feedback_cards);
  not_feedback_cards.forEach((section) => {
    section.content = section.content.filter((node) => !(node.classes && node.classes.includes("feedback_button")));
  });

  const showableCards = options.showCardTest ? regular_cards.filter(options.showCardTest) : regular_cards
  const showableCardUids = showableCards.map((card) => card.uid);
  const zippedShowableCards = zip(showableCards, showableCardUids.slice(1).concat([feedback_cards[0].uid]));

  zippedShowableCards.forEach(([card, next_uid]) => {
    if (!card || !next_uid) { return; }

    const question_sections = card.content.filter((node): node is DocSectionType<InlineNode> => node.type === "section" && !!(node.classes && node.classes.includes("question")));

    question_sections.forEach((question_section) => {
      addSubmitButtonActionsToQuestionSection(question_section);
    });

    const cardWithContinueUid = card as SectionWithContinueUid;
    let continue_button = card.content.filter((node): node is DocButtonType<InlineNode> => node.type === "button" && !!(node.classes && node.classes.includes("continue_button")))[0];
    if (!continue_button) {
      continue_button = continueButton(cardWithContinueUid.continue_button_uid || `continue_button_${card.uid}`);
      card.content = card.content.concat(continue_button);
    }

    continue_button.onClick = findActionOrNew(continue_button.onClick, [
      generateSetNodeKVPairAction(next_uid, "invisible", false),
      generateSetNodeKVPairAction(next_uid, "collapsed", false),
      generateSetNodeKVPairAction(card.uid, "collapsed", true),
      generateSetNodeKVPairAction(continue_button.uid, "invisible", true),
    ]);

    const submit_nodes = compact(question_sections.map((question_section) =>
      question_section.content.find((node): node is DocButtonType<InlineNode> | DocAiAssistantTextInputType<InlineNode> => (node.type === "button" && getText(node.title) === "Submit") || node.type === "aiAssistantTextInput")
    ));
    const old_submit_actions = compact(submit_nodes.flatMap((submit_node) =>
      submit_node.type === "button" ? submit_node.onClick : submit_node.onContinue
    ));
    if (submit_nodes.length > 0) {
      const submit_node = submit_nodes[submit_nodes.length - 1];
      if (submit_node) {
        const new_submit_actions = findActionOrNew(old_submit_actions, generateSetNodeKVPairAction(continue_button.uid, "inactive", 1));

        if (submit_node.type === "button") {
          submit_node.onClick = (submit_node.onClick || []).concat(new_submit_actions);
        } else {
          submit_node.onContinue = (submit_node.onContinue || []).concat(new_submit_actions);
        }

        continue_button.inactive = 2;
      }
    } else {
      delete continue_button.inactive;
    }

    const aiAssistantNodes = card.content.filter((node): node is DocAiAssistantChatType<InlineNode> | DocAiAssistantFeedbackType<InlineNode> => ["aiAssistantChat", "aiAssistantFeedback"].includes(node.type));
    const lastAiAssistantNode = last(aiAssistantNodes);

    if (lastAiAssistantNode) {
      lastAiAssistantNode.onContinue = continue_button.onClick || [];
      lastAiAssistantNode.onContinue = lastAiAssistantNode.onContinue.concat(generateSetNodeKVPairAction(lastAiAssistantNode.uid, "inactive", 2));

      continue_button.invisible = true;
    }
  });

  if (showableCards.length > 0) {
    const feedback_introduction_text = feedbackIntroductionText();

    const last_regular_card = last(showableCards);
    if (last_regular_card) {
      last_regular_card.content.splice(-1, 0, feedback_introduction_text);
    }
  }

  if (!finish_case_text) {
    finish_case_text = finishCaseText();
  }
  finish_case_text.classes = uniq((finish_case_text.classes || []).concat(["finish_case_text"]));
  finish_case_card.content.push(finish_case_text);

  if (!case_summary_button) {
    case_summary_button = caseSummaryButton(documentId);
  }
  finish_case_card.content.push(case_summary_button);

  // Terminal reveals attached to last feedback "Submit & Finish Case".
  let terminalRevealActions: DocAction[] = [
    generateSetNodeKVPairAction(finish_case_text.uid, "invisible", false),
    generateSetNodeKVPairAction(case_summary_button.uid, "invisible", false),
  ];

  release_notes_cards.forEach((card) => {
    if (card["content"].length > 0) {
      terminalRevealActions.push(generateSetNodeKVPairAction(card.uid, "invisible", false));
    }
  });

  learning_objectives_cards.forEach((card) => {
    card.classes = uniq((card.classes || []).concat(["card", "learning_objectives_card"]));
    terminalRevealActions.push(generateSetNodeKVPairAction(card.uid, "invisible", false));
  });

  if (saq_cards.length > 0) {
    if (!finish_case_saq_text) {
      finish_case_saq_text = finishCaseSAQText();
    }
    finish_case_saq_text.classes = uniq((finish_case_saq_text.classes || []).concat(["finish_case_saq_text"]));
    finish_case_card["content"].push(finish_case_saq_text);

    terminalRevealActions.push(generateSetNodeKVPairAction(finish_case_saq_text.uid, "invisible", false));

    saq_cards.forEach((saq_card) => {
      terminalRevealActions.push(generateSetNodeKVPairAction(saq_card.uid, "invisible", false));
    });
  }

  terminalRevealActions.push(generateSetNodeKVPairAction(end_of_case_text.uid, "invisible", false));

  const feedback_cards_uids = feedback_cards.map((card) => card.uid);
  const zipped_feedback_cards = zip(feedback_cards, feedback_cards_uids.slice(1).concat([finish_case_card.uid]));

  zipped_feedback_cards.forEach(([card, next_uid], index) => {
    if (!card || !next_uid) { return; }

    let feedback_button = card.content.find((node): node is DocButtonType<InlineNode> => node.type === "button" && !!(node.classes && node.classes.includes("feedback_button")));
    if (!feedback_button) {
      feedback_button = feedbackSubmitButton(`feedback_card_${index+1}_button`);
      card.content.push(feedback_button);
    }

    const isTerminalFeedback = next_uid === finish_case_card.uid;
    const revealNextActions = [
      generateSetNodeKVPairAction(next_uid, "invisible", false),
      generateSetNodeKVPairAction(next_uid, "collapsed", false),
      generateSetNodeKVPairAction(card.uid, "invisible", true),
      generateSetNodeKVPairAction(card.uid, "collapsed", true),
      ...(isTerminalFeedback ? terminalRevealActions : []),
    ];

    feedback_button.onClick = findActionOrNew(feedback_button.onClick, revealNextActions);

    let rating_bar_1 = card.content.find((node): node is DocRatingBarType => node.uid === "feedback_card_1_rating_bar");
    let rating_bar_2 = card.content.find((node): node is DocRatingBarType => node.uid === "feedback_card_2_rating_bar");

    if (rating_bar_1 && rating_bar_2) {
      feedback_button.inactive = 2
      rating_bar_2.inactive = 2

      rating_bar_1.onClick = findActionOrNew(rating_bar_1.onClick, [
        generateSetNodeKVPairAction(rating_bar_2.uid, "inactive", 1),
      ]);

      rating_bar_2.onClick = findActionOrNew(rating_bar_2.onClick, [
        generateSetNodeKVPairAction(feedback_button.uid, "inactive", 1),
      ]);
    }
  });

  saq_cards.forEach((saq_card) => {
    const question_sections = saq_card.content.filter((node): node is DocSectionType<InlineNode> => node.type === "section" && !!(node.classes && node.classes.includes("question")));
    question_sections.forEach((question_section) => {
      addSubmitButtonActionsToQuestionSection(question_section);
    });
  });

  cards = regular_cards.concat(
    feedback_cards,
    [finish_case_card],
    release_notes_cards,
    learning_objectives_cards,
    saq_cards,
    case_synopsis_cards,
    references_cards,
  );

  cards.forEach((card) => {
    card.invisible = true;
    card.collapsible = true;
    card.collapsed = true;
    card.nested = true;
  });

  if (showableCards[0]) {
    showableCards[0].collapsed = false;
    showableCards[0].invisible = false;
  }

  root_section.content = root_section.content.filter((node) => node.type !== "section");
  root_section.content = root_section.content.concat(cards);

  root_section.content = root_section.content.filter((node) => node.uid !== "end_of_case_text");
  root_section.content = root_section.content.concat([end_of_case_text]);

  return root_section;
}

function getText(node: InlineNode) {
  if (typeof node === "string") {
    return node
  } else {
    return node.text
  }
}

function partitionByClass<T extends DocBlockNodeBase>(nodes: T[], partitionClass: string) {
  return partition(nodes, (node) => !!(node.classes && node.classes.includes(partitionClass)));
}

function endOfCaseText(title: InlineNode) {
  return convertAllInlineNodesToRangeJSON({
    type: "paragraph",
    uid: "end_of_case_text",
    content: `Thank you for completing ${DocConvertPlainText.fromAnyType(title)}.`,
    invisible: true,
  }) as unknown as DocParagraphType<InlineNode>;
}

function finishCaseCard() {
  return convertAllInlineNodesToRangeJSON({
    type: "section",
    uid: "finish_case_card",
    classes: ["card", "finish_case_card"],
    title: "Case Summary Download",
    content: [],
    level: 1,
  }) as unknown as DocSectionType<InlineNode>;
}

function feedbackCard() {
  return convertAllInlineNodesToRangeJSON({
    type: "section",
    uid: `feedback_card_1`,
    classes: ["card", "feedback_card"],
    title: `Feedback`,
    content: [
      {
        type: "paragraph",
        uid: `feedback_paragraph_1`,
        content: "This case was a valuable use of my time.",
      },
      {
        type: "ratingBar",
        uid: `feedback_card_1_rating_bar`,
        maxValue: 5,
      },
      {
        type: "paragraph",
        uid: `feedback_paragraph_2`,
        content: "I will apply what I learned to patient care.",
      },
      {
        type: "ratingBar",
        uid: `feedback_card_2_rating_bar`,
        maxValue: 5,
      },
      {
        type: "section",
        uid: `feedback_card_1_text_input_section`,
        nested: true,
        level: 2,
        title: "Please note any comments, suggestions, or questions related to the case (optional):",
        content: [
          {
            type: "textInput",
            uid: `feedback_card_1_text_input`,
            boxLines: 5,
            characterLimit: 1000,
          },
        ],
      },
      {
        type: "button",
        uid: `feedback_card_1_button`,
        classes: ["feedback_button"],
        title: "Submit & Finish Case",
        onClick: [],
      },
    ],
    level: 1,
  }) as unknown as DocSectionType<InlineNode>;
}

function releaseNotesCards() {
  return [
    convertAllInlineNodesToRangeJSON({
      type: "section",
      uid: "release_notes_card",
      classes: ["card", "release_notes_card"],
      title: "Release Notes",
      content: [],
      level: 1,
    }),
  ];
}

function learningObjectivesCards() {
  return [
    convertAllInlineNodesToRangeJSON({
      type: "section",
      uid: "learning_objectives_card",
      classes: ["card", "learning_objectives_card"],
      title: "Learning Objectives",
      content: [],
      level: 1,
    }),
  ];
}

function caseSynopsisCards() {
  return [
    convertAllInlineNodesToRangeJSON({
      type: "section",
      uid: "case_synopsis_card",
      classes: ["card", "case_synopsis_card"],
      title: "Case Synopsis",
      content: [],
      level: 1,
    }),
  ];
}

function feedbackIntroductionText() {
  return convertAllInlineNodesToRangeJSON({
    uid: "feedback_introduction_text",
    type: "paragraph",
    classes: [
      "feedback_introduction_text",
    ],
    content: "This is the final page of the case. We value your perspective on the learning experience. After completing three required feedback ratings you can finish the case and access the case summary.",
  });
}

function feedbackSubmitButton(uid: string) {
  return convertAllInlineNodesToRangeJSON({
    type: "button",
    uid: uid,
    title: "Submit",
    classes: ["feedback_button"],
    onClick: [],
  });
}

function finishCaseText() {
  return convertAllInlineNodesToRangeJSON({
    type: "paragraph",
    uid: "finish_case_text",
    classes: ["finish_case_text"],
    content: "Well done! You have completed the case. Click to download the case summary.",
    invisible: true,
  });
}

function caseSummaryButton(document_id: number) {
  return convertAllInlineNodesToRangeJSON({
    type: "button",
    uid: "case_summary_button",
    title: "Download Case Summary",
    classes: ["case_summary_button"],
    invisible: true,
    onClick: [
      {
        type: "action",
        action: "openExternalLink",
        arguments: {
          url: `https://medu2.meduapp.com/documents/${document_id}/case_summary.pdf`,
        },
        uid: generateUUID(),
      },
    ],
  });
}

function finishCaseSAQText() {
  return convertAllInlineNodesToRangeJSON({
    type: "paragraph",
    uid: "finish_case_saq_text",
    classes: ["finish_case_saq_text"],
    content: "You may now continue to the optional self-assessment questions for you to assess what you have learned in this case.",
    invisible: true,
  });
}

function addSubmitButtonActionsToQuestionSection(question_section: DocSectionType<InlineNode>) {
  const submit_button = question_section.content.find((node): node is DocButtonType<InlineNode> => node.type === "button" && getText(node.title) === "Submit");
  const ai_assistant_text_input = question_section.content.find((node): node is DocAiAssistantTextInputType<InlineNode> => node.type === "aiAssistantTextInput");

  if (submit_button || ai_assistant_text_input) {
    let old_actions = (submit_button ? submit_button.onClick : ai_assistant_text_input?.onContinue) || [];
    let new_actions: DocAction[] = [];

    const question = question_section.content.find((node) => ["categoryMatcher", "multipleChoice", "textInput", "cdqQuestion"].includes(node.type));
    if (question) {
      new_actions = new_actions.concat(findActionOrNew(old_actions, [
        generateSetNodeKVPairAction(question.uid, "graded", true),
        generateSetNodeKVPairAction(question.uid, "inactive", 2),
      ]));
    }

    const answer_comment = question_section.content.find((node) => node.type === "section" && !!(node.classes && node.classes.includes("answer_comment")));
    if (answer_comment) {
      new_actions = new_actions.concat(findActionOrNew(old_actions, generateSetNodeKVPairAction(answer_comment.uid, "invisible", false)));
      answer_comment.invisible = true;
    }

    if (submit_button) {
      new_actions = new_actions.concat(findActionOrNew(old_actions, generateSetNodeKVPairAction(submit_button.uid, "inactive", 2)));
      submit_button.onClick = new_actions;
    } else if (ai_assistant_text_input) {
      ai_assistant_text_input.onContinue = new_actions;
    }
  }
}

function findActionOrNew(action_array: Array<DocAction> | undefined, new_actions: DocAction | Array<DocAction>) {
  const existing = action_array || [];
  const actions = Array.isArray(new_actions) ? new_actions : [new_actions];

  return actions.map((new_action) => existing.find((old_action) => (old_action.action === new_action.action) && (("arguments" in old_action && old_action.arguments) === ("arguments" in new_action && new_action.arguments))) || new_action);
}

function generateAction(action: ActionName, actionArguments: Record<string, unknown>) {
  return {
    type: "action" as const,
    action: action,
    arguments: actionArguments,
    uid: generateUUID(),
  };
}

function generateSetNodeKVPairAction(uid: string, key: string, value: unknown) {
  return generateAction("setNodeKVPair", {
    uid: uid,
    key: key,
    value: value,
  }) as DocActionSetNodeKVPair;
}

function continueButton(uid: string) {
  return convertAllInlineNodesToRangeJSON({
    type: "button",
    uid: uid,
    title: "Continue",
    classes: ["continue_button"],
    onClick: [],
  });
}

export function unstickStudent(data: DocSectionType<InlineNode>, options: {showCardTest?: (card: DocSectionType<InlineNode>) => boolean} = {}) {
  const cards = data.content.filter((node): node is DocSectionType<InlineNode> => node.type === "section");

  const finish_case_card = cards.find((card) => card.classes?.includes("finish_case_card"))
  if (!finish_case_card || !finish_case_card.invisible) { return data }

  const showableCards = options.showCardTest ? cards.filter(options.showCardTest) : cards
  const lastVisibleCard = findLast(showableCards, (node) => !node.invisible)

  if (!lastVisibleCard) { return data }

  const aiAssistantNodes = (lastVisibleCard.content as Array<DocBlockNode<InlineNode>>).filter((node) => ["aiAssistantChat", "aiAssistantFeedback"].includes(node.type));
  const lastAiAssistantNode = last(aiAssistantNodes);
  if (lastAiAssistantNode) {
    lastAiAssistantNode.inactive = 1
  } else {
    const continueBtn = lastVisibleCard.content.find((node) => node.classes && node.classes.includes("continue_button"));

    if (continueBtn && continueBtn.invisible) {
      continueBtn.invisible = false
    }
    if (continueBtn && continueBtn.inactive === 2 && cardQuestionsAllowContinue(lastVisibleCard)) {
      continueBtn.inactive = 1
    }
  }

  return data
}

function cardQuestionsAllowContinue(card: DocSectionType<InlineNode>) {
  const questions: Array<{ graded?: boolean }> = [];
  const visit = (node: DocBlockNode<InlineNode> | InlineNode) => {
    if (!node || typeof node !== "object" || !("type" in node)) { return; }

    if (node.type === "multipleChoice") {
      if (node.content.some((child) => child.type === "multipleChoiceAnswer")) { questions.push(node); }
    } else if (node.type === "categoryMatcher" || node.type === "textInput" || node.type === "cdqQuestion") {
      questions.push(node);
    }

    if ("content" in node && Array.isArray(node.content)) {
      node.content.forEach(visit);
    }
  };

  card.content.forEach(visit);
  return questions.length === 0 || questions.every((question) => question.graded === true);
}
